// =============================================================================
// hooks/sales/useAppointments.ts — Hook de leitura da agenda comercial
// (`contact_appointments`, F33-S02/F33-S05, doc 26 §9).
//
// Camada de dados REUTILIZÁVEL — vive fora da árvore visual de
// features/sales/agenda/ (doc 27 §3.1) para que outras superfícies (CRM
// F33-S07, Conversas F33-S08 e a futura V3) consultem a mesma agenda sem
// reimplementar a busca. Mesmo padrão de hooks/crm/useLead.ts.
//
// Só GET — criação/edição (POST/PATCH) e as transições complete/reschedule/
// cancel (F33-S03, ainda não mergeadas em main) ficam para os slots donos
// daquelas telas.
// =============================================================================

import type {
  Appointment,
  AppointmentListQuery,
  AppointmentListResponse,
} from '@elemento/shared-schemas';
import { AppointmentListResponseSchema } from '@elemento/shared-schemas';
import { useQuery } from '@tanstack/react-query';

import { api } from '../../lib/api';

export type { Appointment, AppointmentListQuery, AppointmentListResponse };

// ---------------------------------------------------------------------------
// Filtros aceitos pelo hook — subconjunto de AppointmentListQuery que a
// UI efetivamente monta (sortBy/sortOrder/limit/offset sempre explícitos
// pelo chamador; omitir usa o default do schema compartilhado).
// ---------------------------------------------------------------------------

export interface AppointmentsFilters {
  assignedAgentId?: string[];
  /**
   * Filtro por lead (F38-S04, `CommercialSection` do live chat) — o
   * agregador `commercial-context` só devolve o `id` do próximo
   * compromisso; abrir o `AppointmentDrawer` (concluir/reagendar/cancelar)
   * exige o `Appointment` completo, buscado aqui por `leadId` (mesma rota
   * REST que a Agenda já usa — `AppointmentListQuerySchema.leadId` existe
   * desde F33-S02, só não estava mapeado neste hook até agora).
   */
  leadId?: string[];
  channel?: AppointmentListQuery['channel'];
  campaignMemberId?: string[];
  status?: AppointmentListQuery['status'];
  scheduledFrom?: string;
  scheduledTo?: string;
  sortBy?: AppointmentListQuery['sortBy'];
  sortOrder?: AppointmentListQuery['sortOrder'];
  limit?: number;
  offset?: number;
}

// ---------------------------------------------------------------------------
// Query key factory
// ---------------------------------------------------------------------------

export const appointmentKeys = {
  all: ['sales', 'appointments'] as const,
  list: (filters: AppointmentsFilters) => ['sales', 'appointments', 'list', filters] as const,
};

// ---------------------------------------------------------------------------
// Querystring — array params seguem a convenção scalar→array do backend
// (packages/shared-schemas/src/sales-appointments.ts arrayQueryParam):
// múltiplos valores viram múltiplas entradas `?status=a&status=b`, nunca CSV.
// ---------------------------------------------------------------------------

export function buildAppointmentsQuery(filters: AppointmentsFilters): string {
  const qs = new URLSearchParams();

  for (const id of filters.assignedAgentId ?? []) qs.append('assignedAgentId', id);
  for (const id of filters.leadId ?? []) qs.append('leadId', id);
  for (const c of filters.channel ?? []) qs.append('channel', c);
  for (const id of filters.campaignMemberId ?? []) qs.append('campaignMemberId', id);
  for (const s of filters.status ?? []) qs.append('status', s);

  if (filters.scheduledFrom !== undefined) qs.set('scheduledFrom', filters.scheduledFrom);
  if (filters.scheduledTo !== undefined) qs.set('scheduledTo', filters.scheduledTo);
  if (filters.sortBy !== undefined) qs.set('sortBy', filters.sortBy);
  if (filters.sortOrder !== undefined) qs.set('sortOrder', filters.sortOrder);
  if (filters.limit !== undefined) qs.set('limit', String(filters.limit));
  if (filters.offset !== undefined) qs.set('offset', String(filters.offset));

  return qs.toString();
}

// ---------------------------------------------------------------------------
// Fetcher
// ---------------------------------------------------------------------------

async function fetchAppointments(filters: AppointmentsFilters): Promise<AppointmentListResponse> {
  const query = buildAppointmentsQuery(filters);
  const raw = await api.get<unknown>(`/api/sales/appointments${query ? `?${query}` : ''}`);
  // Validação runtime: a agenda mistura dados de vários módulos (lead,
  // campanha, agente) — parse defensivo evita que um drift de contrato
  // silencioso vire tela quebrada (doc 26 §9, mesmo padrão de useAgents.ts).
  return AppointmentListResponseSchema.parse(raw);
}

// ---------------------------------------------------------------------------
// useAppointments — lista paginada com filtros
// ---------------------------------------------------------------------------

export interface UseAppointmentsOptions {
  /** Desabilita o fetch (ex: aguardando gate de permissão/flag). Default true. */
  enabled?: boolean;
}

export function useAppointments(
  filters: AppointmentsFilters,
  opts: UseAppointmentsOptions = {},
): {
  data: AppointmentListResponse | undefined;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
} {
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: appointmentKeys.list(filters),
    queryFn: () => fetchAppointments(filters),
    enabled: opts.enabled ?? true,
    placeholderData: (prev) => prev,
  });

  return { data, isLoading, isError, error: error as Error | null, refetch };
}

// ---------------------------------------------------------------------------
// useAppointmentsCount — total de uma janela de filtro, sem carregar a
// lista inteira (limit=1 — só o campo `total` importa). Usado pela faixa de
// indicadores da agenda, que precisa de 4 números simultâneos independente
// da aba ativa.
// ---------------------------------------------------------------------------

export function useAppointmentsCount(
  filters: Omit<AppointmentsFilters, 'limit' | 'offset'>,
  opts: UseAppointmentsOptions = {},
): { total: number | undefined; isLoading: boolean; isError: boolean } {
  const countFilters: AppointmentsFilters = { ...filters, limit: 1, offset: 0 };
  const { data, isLoading, isError } = useQuery({
    queryKey: appointmentKeys.list(countFilters),
    queryFn: () => fetchAppointments(countFilters),
    enabled: opts.enabled ?? true,
    placeholderData: (prev) => prev,
  });

  return { total: data?.total, isLoading, isError };
}
